/* File: YerevanWeather.jsx
 * Path: ananunadm/src/components/main/YerevanWeather.jsx
 */
import { useEffect, useMemo, useState } from "react";
import {
	Badge,
	Box,
	Button,
	Divider,
	Group,
	Loader,
	Popover,
	SimpleGrid,
	Stack,
	Text,
	useMantineTheme
} from "@mantine/core";
import { T } from "@/helpers/translator";

export const YEREVAN_WEATHER_URL =
	"https://api.open-meteo.com/v1/forecast?latitude=40.1792&longitude=44.4991&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&hourly=temperature_2m,precipitation_probability,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,relative_humidity_2m_mean&timezone=Asia%2FYerevan&forecast_days=16";

/** WMO code → icon type + translator key (use T(labelKey, lang) for the label). */
export function getWeatherCondition(code) {
	if (code === 0) return { labelKey: "weatherClear", type: "sun" };
	if ([1, 2].includes(code)) return { labelKey: "weatherPartlyCloudy", type: "partly" };
	if (code === 3) return { labelKey: "weatherCloudy", type: "cloud" };
	if ([45, 48].includes(code)) return { labelKey: "weatherFog", type: "fog" };
	if ([51, 53, 55, 56, 57].includes(code)) return { labelKey: "weatherDrizzle", type: "rain" };
	if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { labelKey: "weatherRain", type: "rain" };
	if ([71, 73, 75, 77, 85, 86].includes(code)) return { labelKey: "weatherSnow", type: "snow" };
	if ([95, 96, 99].includes(code)) return { labelKey: "weatherThunderstorm", type: "storm" };
	return { labelKey: "weatherUnknown", type: "cloud" };
}

/** Per-condition icon color — sun/partly use header orange/yellow. */
export function getWeatherIconColor(type) {
	switch (type) {
		case "sun":
			return "#fd7e14";
		case "partly":
			return "#fab005";
		case "cloud":
			return "#868e96";
		case "rain":
			return "#228be6";
		case "snow":
			return "#339af0";
		case "storm":
			return "#7950f2";
		case "fog":
			return "#adb5bd";
		default:
			return "#fd7e14";
	}
}

export function ColoredWeatherIcon({ type, size = 22, className, title }) {
	return (
		<span
			className={className}
			title={title}
			style={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				flex: "0 0 auto",
				color: getWeatherIconColor(type)
			}}
		>
			<WeatherSvg type={type} size={size} />
		</span>
	);
}

export function WeatherSvg({ type, size = 22 }) {
	const common = {
		width: size,
		height: size,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: "1.8",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		"aria-hidden": true
	};

	if (type === "sun")
	{
		return (
			<svg {...common}>
				<circle cx="12" cy="12" r="4" />
				<path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
			</svg>
		);
	}

	if (type === "partly")
	{
		return (
			<svg {...common}>
				<path d="M8 5V3M3.76 6.76 2.34 5.34M2 12H0M3.76 17.24l-1.42 1.42M8 21v-2M14.24 6.76l1.42-1.42" />
				<circle cx="8" cy="12" r="4" />
				<path d="M13 18h5.5a3.5 3.5 0 0 0 .43-6.97A5 5 0 0 0 9.6 9.2 4 4 0 0 0 13 18Z" />
			</svg>
		);
	}

	if (type === "rain")
	{
		return (
			<svg {...common}>
				<path d="M7 16.5h10.5a4.5 4.5 0 0 0 .47-8.98A6 6 0 0 0 6.7 6.27 4.8 4.8 0 0 0 7 16.5Z" />
				<path d="M8 19l-1 2M13 19l-1 2M18 19l-1 2" />
			</svg>
		);
	}

	if (type === "snow")
	{
		return (
			<svg {...common}>
				<path d="M7 15.5h10.5a4.5 4.5 0 0 0 .47-8.98A6 6 0 0 0 6.7 5.27 4.8 4.8 0 0 0 7 15.5Z" />
				<path d="M8 19h.01M12 21h.01M16 19h.01" />
			</svg>
		);
	}

	if (type === "storm")
	{
		return (
			<svg {...common}>
				<path d="M7 15.5h10.5a4.5 4.5 0 0 0 .47-8.98A6 6 0 0 0 6.7 5.27 4.8 4.8 0 0 0 7 15.5Z" />
				<path d="m12 16-2 4h3l-1 3 4-5h-3l1-2Z" />
			</svg>
		);
	}

	if (type === "fog")
	{
		return (
			<svg {...common}>
				<path d="M7 14h10.5a4.5 4.5 0 0 0 .47-8.98A6 6 0 0 0 6.7 3.77 4.8 4.8 0 0 0 7 14Z" />
				<path d="M4 17h16M6 20h12" />
			</svg>
		);
	}

	return (
		<svg {...common}>
			<path d="M6.5 18h11a5 5 0 0 0 .52-9.97A6.5 6.5 0 0 0 5.84 6.66 5.7 5.7 0 0 0 6.5 18Z" />
		</svg>
	);
}

function formatHour(date, lang) {
	return new Intl.DateTimeFormat(lang || "en", {
		hour: "2-digit",
		minute: "2-digit"
	}).format(new Date(date));
}

function formatDay(date, lang) {
	return new Intl.DateTimeFormat(lang || "en", { weekday: "short" }).format(
		new Date(`${date}T12:00:00`)
	);
}

export default function YerevanWeather({ isMobile = false, lang = "en" }) {
	const theme = useMantineTheme();
	const [opened, setOpened] = useState(false);
	const [weather, setWeather] = useState(null);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let mounted = true;

		async function loadWeather() {
			try
			{
				const response = await fetch(YEREVAN_WEATHER_URL);
				if (!response.ok) throw new Error("Weather API error");
				const data = await response.json();
				if (mounted)
				{
					setWeather(data);
					setError(false);
					setLoading(false);
				}
			} catch
			{
				if (mounted)
				{
					setError(true);
					setLoading(false);
				}
			}
		}

		loadWeather();
		const intervalId = window.setInterval(loadWeather, 10 * 60 * 1000);

		return () => {
			mounted = false;
			window.clearInterval(intervalId);
		};
	}, []);

	const current = weather?.current;
	const condition = getWeatherCondition(current?.weather_code);
	const conditionLabel = T(condition.labelKey, lang);
	const isDark = theme.colorScheme === "dark";

	const nextHours = useMemo(() => {
		return (
			weather?.hourly?.time
				?.map((time, index) => ({
					time,
					temp: weather.hourly.temperature_2m?.[index],
					rainChance: weather.hourly.precipitation_probability?.[index],
					code: weather.hourly.weather_code?.[index]
				}))
				.filter((item) => item.time >= (current?.time || ""))
				.slice(0, 6) || []
		);
	}, [weather, current?.time]);

	const dailyForecast = useMemo(() => {
		return (
			weather?.daily?.time?.slice(0, 5).map((date, index) => ({
				date,
				code: weather.daily.weather_code?.[index],
				max: weather.daily.temperature_2m_max?.[index],
				min: weather.daily.temperature_2m_min?.[index],
				rain: weather.daily.precipitation_sum?.[index]
			})) || []
		);
	}, [weather]);

	return (
		<Popover
			opened={opened}
			onChange={setOpened}
			position="bottom-end"
			width={isMobile ? 320 : 380}
			shadow="xl"
			withArrow
			withinPortal
			middlewares={{ flip: false, shift: true, inline: false }}
		>
			<Popover.Target>
				<Button
					variant="subtle"
					size={isMobile ? "sm" : "md"}
					onClick={() => setOpened((value) => !value)}
					aria-label={T("weatherAriaYerevanForecast", lang)}
					aria-expanded={opened}
					style={{
						margin: 0,
						padding: isMobile ? 8 : "7px 10px",
						minWidth: isMobile ? 38 : 104,
						color: error ? theme.colors.red[5] : theme.colors.orange[5]
					}}
				>
					<Group spacing={isMobile ? 0 : 6} noWrap>
						{loading ? (
							<Loader size="xs" color="orange" />
						) : (
							<ColoredWeatherIcon type={condition.type} size={isMobile ? 20 : 22} />
						)}
						{!isMobile && (
							<Stack spacing={0} align="flex-start">
								<Text size={10} color="dimmed" lh={1}>
									{T("weatherCityYerevan", lang)}
								</Text>
								<Text size="xs" weight={800} lh={1.1} color={isDark ? "gray.1" : "dark.8"}>
									{current
										? `${Math.round(current.temperature_2m)} ${T("weatherTempUnit", lang)}`
										: T("weatherLoadingShort", lang)}
								</Text>
							</Stack>
						)}
					</Group>
				</Button>
			</Popover.Target>
			<Popover.Dropdown
				p="md"
				sx={{
					background: isDark ? "rgba(15, 18, 28, 0.96)" : "rgba(255,255,255,0.98)",
					borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"
				}}
			>
				<Group position="apart" align="flex-start" noWrap mb="sm">
					<Box>
						<Text weight={800} size="sm">
							{T("weatherHeadingYerevan", lang)}
						</Text>
						<Text size="xs" color="dimmed">
							{error ? T("weatherForecastUnavailable", lang) : conditionLabel}
						</Text>
					</Box>
					<Text size={28} weight={900} color={error ? "red" : "teal"} lh={1}>
						{current
							? `${Math.round(current.temperature_2m)} ${T("weatherTempUnit", lang)}`
							: "--"}
					</Text>
				</Group>

				{current && (
					<Group spacing="xs" mb="sm">
						<Badge variant="light" color="cyan">
							{T("weatherHumidity", lang)} {current.relative_humidity_2m}%
						</Badge>
						<Badge variant="light" color="orange">
							{T("weatherWind", lang)} {Math.round(current.wind_speed_10m)} {T("weatherWindUnit", lang)}
						</Badge>
					</Group>
				)}

				{nextHours.length > 0 && (
					<>
						<Text size="xs" weight={700} color="dimmed" mb={6}>
							{T("weatherNextHours", lang)}
						</Text>
						<SimpleGrid cols={6} spacing={6} mb="sm">
							{nextHours.map((hour) => {
								const hourCondition = getWeatherCondition(hour.code);

								return (
									<Stack
										key={hour.time}
										align="center"
										spacing={3}
										p={6}
										sx={{
											borderRadius: theme.radius.md,
											background: isDark ? theme.colors.dark[6] : theme.colors.gray[0],
											border: `1px solid ${
												isDark ? theme.colors.dark[5] : theme.colors.gray[2]
											}`
										}}
									>
										<Text size={10} color="dimmed">
											{formatHour(hour.time, lang)}
										</Text>
										<ColoredWeatherIcon type={hourCondition.type} size={17} />
										<Text size="xs" weight={800}>
											{Math.round(hour.temp)} {T("weatherTempUnit", lang)}
										</Text>
										<Text size={10} color="dimmed">
											{hour.rainChance}%
										</Text>
									</Stack>
								);
							})}
						</SimpleGrid>
					</>
				)}

				{dailyForecast.length > 0 && (
					<>
						<Divider my="xs" />
						<Stack spacing={5}>
							{dailyForecast.map((day) => {
								const dayCond = getWeatherCondition(day.code);
								const dayCondLabel = T(dayCond.labelKey, lang);
								return (
									<Group key={day.date} position="apart" noWrap>
										<Group spacing={6} noWrap style={{ minWidth: 108 }}>
											<Text size="xs" weight={700} style={{ minWidth: 32 }}>
												{formatDay(day.date, lang)}
											</Text>
											<ColoredWeatherIcon
												type={dayCond.type}
												size={16}
												title={dayCondLabel}
											/>
											<Text size={10} color="dimmed" truncate>
												{dayCondLabel}
											</Text>
										</Group>
										<Text size="xs" weight={700}>
											{Math.round(day.max)} {T("weatherTempUnit", lang)} / {Math.round(day.min)}{" "}
											{T("weatherTempUnit", lang)}
										</Text>
										<Text size="xs" color="dimmed" style={{ minWidth: 42, textAlign: "right" }}>
											{day.rain} {T("weatherPrecipUnit", lang)}
										</Text>
									</Group>
								);
							})}
						</Stack>
					</>
				)}
			</Popover.Dropdown>
		</Popover>
	);
}
